import { Bill } from '@features/bills/types/bill'
import { ReconcilableResponse } from '@features/paymentReconciliationModal/query-api'
import { Invoice } from '@modules-deprecated/app/invoices/types'
import {
  BankLine,
  BankLineMatch,
  Posting,
  ReconciliationSuggestionsResponse,
} from '@modules-deprecated/bankReconciliation/types'

import { Side } from '../../enums/side'
import { Metable } from '../../types/metable'
import { getRequest, postRequest, putRequest } from '../../utils/api'
import { ExternalInvoiceFromBilly } from '../invoices/types/externalInvoiceFromBilly'
import { Transaction } from '../transactions/types/transaction'
import { BankMatchObjectSource, BankMatchUiFlow, BankMatchUiFlowValue, UiSource, UiSourceValue } from './enums'
import { getUiSourceTrackingHeaders } from './utils/getUiSourceTrackingHeaders'

export const unMatchBankLineMatch = (
  matchId: string,
  uiSource?: UiSourceValue,
  uiFlow?: BankMatchUiFlowValue,
): Promise<void> => {
  return postRequest(`/v2/bankLineMatches/${matchId}/unreconcile`, {}, getUiSourceTrackingHeaders({ uiSource, uiFlow }))
}

export interface MarkBankLinesAsPrivateParams {
  organizationId: string
  bankLineIds: string[]
}

export const markBankLinesAsPrivate = ({
  organizationId,
  bankLineIds,
}: MarkBankLinesAsPrivateParams): Promise<ReconcilableResponse> => {
  return postRequest(
    `/v2/organizations/${organizationId}/reconcilable/private`,
    { bankLineIds },
    getUiSourceTrackingHeaders({
      uiSource: UiSource.AccountingTransactionView,
      uiFlow: BankMatchUiFlow.Created,
      objectSource: BankMatchObjectSource.LedgerEntry,
    }),
  )
}

export interface FetchInvoicesForMatchingResponse extends Metable {
  invoices: Invoice[]
}

export const fetchInvoicesForMatching = async (queryString: string): Promise<FetchInvoicesForMatchingResponse> => {
  return getRequest(`/v2/invoices?${queryString}`)
}

export interface FetchBillsForMatchingResponse extends Metable {
  bills: Bill[]
}

export const fetchBillsForMatching = async (queryString: string): Promise<FetchBillsForMatchingResponse> => {
  return getRequest(`/v2/bills?${queryString}`)
}

export interface FetchExternalInvoicesForMatchingResponse extends Metable {
  externalInvoices: ExternalInvoiceFromBilly[]
}

export const fetchExternalInvoicesForMatching = async (
  queryString: string,
): Promise<FetchExternalInvoicesForMatchingResponse> => {
  return getRequest(`/v2/externalInvoices?${queryString}`)
}

export interface FetchPostingsForMatchingResponse extends Metable {
  postings: Posting[]
  transactions?: Transaction[]
}

export const fetchPostingsForMatching = async (queryString: string): Promise<FetchPostingsForMatchingResponse> => {
  return getRequest(`/v2/postings?${queryString}`)
}

export interface GetBankLineMatchesParams {
  organizationId: string
  accountId: string
  bankLineIds: string[]
}

export const getBankLineMatchSuggestions = async ({
  organizationId,
  accountId,
  bankLineIds,
}: GetBankLineMatchesParams): Promise<ReconciliationSuggestionsResponse> => {
  return postRequest(`/api/matching-service/${organizationId}/matches/${accountId}/bank_lines`, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    bank_line_ids: bankLineIds,
  })
}

export interface CombineBankLinesParams {
  bankLineId: string
  matchId: string
}

export interface CombineBankLinesResponse extends Metable {
  bankLineMatches: BankLineMatch[]
  bankLines: BankLine[]
}

export const combineBankLines = async ({
  bankLineId,
  matchId,
}: CombineBankLinesParams): Promise<CombineBankLinesResponse> => {
  return putRequest(`/v2/bankLines/${bankLineId}`, {
    bankLine: {
      id: bankLineId,
      matchId,
    },
  })
}

export interface CreateBankLineMatchParams {
  accountId: string
  entryDate: string
  amount: number
  side: Side
}

export interface CreateBankLineMatchResponse extends Metable {
  bankLineMatches: BankLineMatch[]
}

export const createBankLineMatch = (params: CreateBankLineMatchParams): Promise<CreateBankLineMatchResponse> => {
  return postRequest('/v2/bankLineMatches', {
    bankLineMatch: {
      ...params,
      isApproved: false,
    },
  })
}
